import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, Avatar, EmptyState, NBTextarea } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, Mail, Copy, Check, UserPlus } from "lucide-react";

const COLORS = ["#E8B96B", "#B8CFA9", "#E9B5A0", "#A8BFD1", "#C28F4E", "#C75D3F"];

export default function Members() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [inviteFor, setInviteFor] = useState(null); // member object
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: "", role: "Member", dob: "", email: "", phone: "", bio: "", avatar_color: "#E8B96B" });

  const load = async () => {
    const { data } = await api.get("/api/members");
    setMembers(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/members", form);
      toast.success("Member added!");
      setOpen(false);
      setForm({ name: "", role: "Member", dob: "", email: "", phone: "", bio: "", avatar_color: "#E8B96B" });
      load();
    } catch (err) {
      toast.error("Failed to add member");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    await api.delete(`/api/members/${id}`);
    toast.success("Member removed");
    load();
  };

  const startInvite = (member) => {
    setInviteFor(member);
    setInviteEmail(member.email || "");
    setInviteLink("");
    setCopied(false);
  };

  const generateInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/api/invites", {
        member_id: inviteFor.id,
        email: inviteEmail,
        app_url: window.location.origin,
      });
      const link = `${window.location.origin}/invite/${data.code}`;
      setInviteLink(link);
      if (data.email_sent) {
        toast.success(`Invite emailed to ${inviteEmail}!`);
      } else {
        toast.success("Invite created — share the link manually");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invite");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="members-page">
      <PageHeader
        title="Family Members"
        subtitle="Profiles for everyone in your household."
        action={<NBButton testId="add-member-btn" onClick={() => setOpen(true)}><Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Add Member</NBButton>}
      />

      {members.length === 0 ? (
        <EmptyState title="No members yet" subtitle="Add your first family member to get started." action={<NBButton onClick={() => setOpen(true)}>Add member</NBButton>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {members.map((m) => (
            <NBCard key={m.id} color={m.avatar_color} data-testid={`member-card-${m.id}`}>
              <div className="flex items-start justify-between">
                <Avatar name={m.name} color="white" size={56} />
                <div className="flex gap-1">
                  {!m.is_account && (
                    <button onClick={() => startInvite(m)} className="nb-btn bg-white p-1.5" data-testid={`invite-member-${m.id}`} title="Invite">
                      <UserPlus strokeWidth={2.5} className="w-4 h-4" />
                    </button>
                  )}
                  {!m.is_account && (
                    <button onClick={() => remove(m.id)} className="nb-btn bg-white p-1.5" data-testid={`delete-member-${m.id}`}>
                      <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <h3 className="text-2xl font-black mt-4" style={{ fontFamily: "var(--hh-display)" }}>{m.name}</h3>
              <div className="flex gap-1.5 mt-1 flex-wrap">
                <span className="nb-badge bg-white">{m.role}</span>
                {m.is_account && <span className="nb-badge bg-[#B8CFA9]">✓ Joined</span>}
                {m.invite_code && !m.is_account && <span className="nb-badge bg-white">Invited</span>}
              </div>
              {m.dob && <p className="text-sm font-bold mt-2">🎂 {m.dob}</p>}
              {m.email && <p className="text-sm mt-1 truncate">{m.email}</p>}
              {m.phone && <p className="text-sm">{m.phone}</p>}
              {m.bio && <p className="text-sm mt-3 italic">"{m.bio}"</p>}
            </NBCard>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Member</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="member-name" placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <NBSelect testId="member-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option>Parent</option><option>Child</option><option>Teen</option><option>Grandparent</option><option>Member</option>
              </NBSelect>
              <NBInput testId="member-dob" type="date" placeholder="Date of birth" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              <NBInput testId="member-email" type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <NBInput testId="member-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <NBTextarea testId="member-bio" placeholder="Short bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-2 block">Avatar Color</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((c) => (
                    <button type="button" key={c} onClick={() => setForm({ ...form, avatar_color: c })}
                      className={`w-10 h-10 rounded-full border-2 border-black ${form.avatar_color === c ? "nb-shadow" : ""}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <NBButton testId="submit-member" type="submit" className="w-full">Add Member</NBButton>
            </form>
          </div>
        </div>
      )}

      {inviteFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setInviteFor(null)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Invite {inviteFor.name}</h2>
              <button onClick={() => setInviteFor(null)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            {!inviteLink ? (
              <form onSubmit={generateInvite} className="space-y-3">
                <p className="text-sm text-neutral-700">Generate a private invite link. {inviteFor.name} can use it to set their password and join the family.</p>
                <div>
                  <label className="text-xs font-black uppercase tracking-wider mb-1 block">Email</label>
                  <NBInput testId="invite-email-input" required type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="member@example.com" />
                </div>
                <NBButton testId="generate-invite" type="submit" color="#B8CFA9" className="w-full">
                  <Mail strokeWidth={3} className="w-4 h-4 inline mr-1" /> Generate Link
                </NBButton>
              </form>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-bold">Share this link with {inviteFor.name}:</p>
                <div className="nb-card !p-3 !shadow-none break-all text-xs font-mono bg-[#F6EFE4]" data-testid="invite-link">
                  {inviteLink}
                </div>
                <button onClick={copyLink} data-testid="copy-invite-link" className="nb-btn bg-[#E8B96B] w-full px-4 py-2 text-sm">
                  {copied ? <><Check strokeWidth={3} className="w-4 h-4 inline mr-1" /> Copied!</> : <><Copy strokeWidth={3} className="w-4 h-4 inline mr-1" /> Copy Link</>}
                </button>
                <p className="text-xs text-neutral-600 text-center">The link is single-use and gives access only to {inviteFor.name}'s profile.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
